<template>
    <div class="flex flex-col h-full w-full gap-2 px-2">
        <DxTreeList
            id="ai-tree-node-load"
            ref="treeListRef"
            :allow-column-reordering="true"
            :allow-column-resizing="true"
            :column-auto-width="true"
            :data-source="dataSet"
            :expanded-row-keys="expandedRowKeys"
            :focused-row-enabled="true"
            :focusedRowEnabled="true"
            :focusedRowKey="focusedRowKey"
            :has-items-expr="meta.hasItemsField"
            :key-expr="meta.keyExpr"
            :parent-id-expr="meta.keyParent"
            :root-value="''"
            :show-borders="true"
            :show-row-lines="true"
            :showColumnLines="true"
            :sorting="{ mode: 'None' }"
            :word-wrap-enabled="meta?.wordWrapEnabled ?? true"
            class="h-0 grow custom-toolbar"
            column-resizing-mode="'nextColumn'"
            @row-expanded="onRowExpanded"
            @row-expanding="onRowExpanding"
            @cell-click="onCellClick"
            @focused-row-changed="onFocusedRowChanged"
        >
            <dx-toolbar>
                <dx-item
                    v-for="(item, index) in toolbarConfigAfter"
                    :key="index"
                    :name="item.component.name"
                    location="after"
                >
                    <dx-button
                        v-if="item.widget === 'dx-button'"
                        :hint="item.component.hint"
                        class="ai-button-base"
                        @click="
                            event => {
                                event['item'] = item;
                                event['eventName'] = 'click';
                                eventController(event);
                            }
                        "
                    >
                        <component :is="item.component.htmlElement" />
                    </dx-button>
                    <dx-text-box
                        v-if="item.widget === 'dx-text-box'"
                        v-model:value="refs[item.component.valueRefName.name]"
                        :label="
                            refs[item.component.valueLabelName]
                                ? refs[item.component.valueLabelName]
                                : item.component.valueLabelName
                        "
                        :width="item.component.width"
                        class="ai-toolbar-texteditor"
                        @enter-key="
                            event => {
                                event['item'] = item;
                                event['eventName'] = 'enter-key';
                                eventController(event);
                            }
                        "
                        @key-up="
                            event => {
                                event['item'] = item;
                                event['eventName'] = 'key-up';
                                eventController(event);
                            }
                        "
                    >
                        <DxTextBoxButton
                            v-for="(inputButton, indexButton) in item.component.inputButtons"
                            :key="indexButton"
                            :name="inputButton.name"
                            :options="inputButton.options"
                            location="after"
                        />
                    </dx-text-box>
                </dx-item>
            </dx-toolbar>
            <dx-column
                v-if="meta.firstColumnFields"
                :allow-reordering="false"
                :caption="meta.firstColumnFields.caption"
                :cell-template="'firstColumnCellTemplate'"
                :column-min-width="400"
                :data-field="meta.firstColumnFields.text"
                :width="meta.firstColumnFields.width"
                :editor-options="{
                    showClearButton: isDate(item),
                }"
                headerCellTemplate="headerTemplate"
            />
            <dx-column
                v-for="(item, index) in meta.columns"
                :key="index"
                :allow-reordering="meta.firstColumnFields || index > 1"
                :caption="item.caption"
                :cell-template="item.cellTemplate"
                :data-field="item.field"
                :editor-options="{
                    maxLength: item.maxLength,
                }"
                :visible="item.visible"
                :width="item.width"
                headerCellTemplate="headerTemplate"
            />
            <template #cellImg="{ data }">
                <div style="display: flex">
                    <component :is="data.text" />
                    <span style="padding-left: 10px">{{ data.text }}</span>
                </div>
            </template>
            <template #firstColumnCellTemplate="{ data }">
                <div style="display: flex">
                    <component :is="data.data[meta.firstColumnFields.svg]" />
                    <span style="padding-left: 10px">{{ data.data[meta.firstColumnFields.text] }}</span>
                </div>
            </template>
            <template v-for="(_, slot) in $slots" #[slot]>
                <slot :name="slot" />
            </template>
            <template #headerTemplate="{ data: headerInfo }">
                <div class="h-full flex items-start whitespace-normal break-words text-left line-clamp-3">
                    {{ headerInfo.column.caption }}
                </div>
            </template>
        </DxTreeList>
        <ai-grid-virtual-footer :show-selected="false" :status-message="statusMessage" :total-count="totalCount" />
    </div>
</template>
<script setup>
/* Чтобы ограничить количество символов, которые можно добавить в поле ввода, необходимо в мету колонки прописать настройку 'maxLength: 250' (например, 250 символов)*/
/*
Чтобы слова в заголовках столбцов НЕ переносились необходимо добавить в мету этой таблицы настройку 'wordWrapEnabled: false,'
*/
import { onBeforeMount, onMounted, ref } from 'vue';

import { DxColumn, DxItem, DxToolbar, DxTreeList } from 'devextreme-vue/tree-list';
import { DxButton } from 'devextreme-vue';
import { DxButton as DxTextBoxButton, DxTextBox } from 'devextreme-vue/text-box';

import {
    aiTreeNodeLoadStore,
    aiTreeNodeLoadStoreLocalGet,
    aiTreeNodeLoadStoreLocalSet,
    aiTreeNodeLoadStoreLocalSetProp,
} from '@/components/ai-tree-node-load/ai-tree-node-load-store';
import { getUserDataChanges, saveUserDataChanges } from '@/service/userdata-service';
import AiGridVirtualFooter from '@/components/ai-grid-virtual/ai-grid-virtual-footer.vue';

const disabledStart = ref(false);
const disabledPrev = ref(false);
const disabledNext = ref(false);

const findResult = ref('Введите текст');
const findValue = ref('');
const refs = ref({
    findValue: findValue,
    findResult: findResult,
});
const toolbarSystemItemsNames = ['refresh', 'find-prev', 'find', 'find-start', 'find-next'];

const emit = defineEmits(['toolbarButtonClick', 'toolbarTextboxEvent']);

const props = defineProps({
    meta: {
        type: Object,
        default: () => {
            return {};
        },
    },
    selectFuncParamsCallBack: {
        type: Function,
        default: async () => {},
    },
    rowExpandingCallBack: {
        type: Function,
        default: async () => {},
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => [],
    },
    getValueRefsFuncCallBack: {
        type: Function,
        default: () => {},
    },
});

onBeforeMount(() => {
    // check props
    if (!props.toRemoveToolbarItemsNames) {
        Error('props.toRemoveToolbarItemsNames value error, be9fb310-7f60-4e3f-81d6-eed5475f5858');
    }
    if (typeof props.toRemoveToolbarItemsNames !== 'object') {
        Error('props.toRemoveToolbarItemsNames value error, 1bd9089b-2a34-49b5-8942-2adc59198eeb');
    }
    if (!Array.isArray(props.toRemoveToolbarItemsNames)) {
        Error('props.toRemoveToolbarItemsNames value error, ba1de4d6-bd82-4f1d-97b5-0adda299b745');
    }
    for (const i in props.toRemoveToolbarItemsNames) {
        if (typeof props.toRemoveToolbarItemsNames[i] !== 'string') {
            Error(
                `props.toRemoveToolbarItemsNames element index ${i} value error, 1bd9089b-2a34-49b5-8942-2adc59198eeb`
            );
        }
    }
    for (const s of props.toRemoveToolbarItemsNames) {
        if (!toolbarSystemItemsNames.includes(s)) {
            Error(
                `element ${s} is not the name of a system toolbar element, value error, 53e8b860-271c-4390-b119-04090f1d2370`
            );
        }
    }

    //
    for (const item of toolbarConfigInitAfter) {
        if (!props.toRemoveToolbarItemsNames.includes(item.component.name)) {
            toolbarConfigAfter.push({ ...item });
        }
    }
    const compare = (a, b) => {
        if (a.index < b.index) {
            return -1;
        }
        if (a.index > b.index) {
            return 1;
        }
        return 0;
    };
    const toolbarUserItems = [...props.toolbarUserItems].sort(compare);

    for (const item of toolbarUserItems) {
        if (item.component.valueRefName) {
            refs.value[item.component.valueRefName.name] = item.component.valueRefName.value;
        }
        toolbarConfigAfter.splice(item.index, 0, item);
    }
    props.getValueRefsFuncCallBack(getValueRefs);
});

onMounted(async () => {
    const userDataResult = await getUserDataChanges();
    if (userDataResult.result) {
        if (userDataResult.changes?.findValue) {
            findValue.value = userDataResult.changes?.findValue;
        } else {
            findValue.value = '';
        }
    }
    sort.value = { ...props.meta.sort };
    treeListRef.value.instance.option('sorting', {
        mode: 'single',
        ascending: sort.value.order === 'asc' ? true : false,
        sortBy: [sort.value.field],
    });
    treeListRef.value.instance.option('scrolling', {
        mode: 'standard',
        showScrollbar: 'always',
    });
    await refreshLocal();
});

let toolbarConfigAfter = [];
// removeToolbarItemsNames ['refresh', 'find-prev', 'find', 'find-start', 'find-next']
const toolbarConfigInitAfter = [
    {
        widget: 'dx-button',
        component: {
            name: 'refresh',
            hint: 'Обновить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-refresh-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'find-prev',
            hint: 'Предыдущий',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-find-prev-base',
        },
    },
    {
        widget: 'dx-text-box',
        component: {
            name: 'find',
            emits: [{ name: 'enter-key' }, { name: 'key-up' }],
            htmlElement: 'ai-find-prev-base',
            valueLabelName: 'findResult',
            valueRefName: { name: 'findValue' },
            width: 250,
            inputButtons: [
                {
                    name: 'filter-clear',
                    options: {
                        icon: `    <svg class='ai-clear-button-color' width='22' height='22' viewBox='-4 -5 22 22' xmlns='http://www.w3.org/2000/svg'>
                        <rect x='11.2729' width='1.5' height='15' rx='0.5' transform='rotate(45 11.2729 0)'/>
                        <rect x='0.666504' y='1.06055' width='1.5' height='15' rx='0.5' transform='rotate(-45 0.666504 1.06055)'/>
                       </svg>`,
                        stylingMode: 'text',
                        hint: 'Очистить',
                        onClick: () => {
                            findValue.value = '';
                            aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findValue', findValue.value);
                            aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findPosition', undefined);
                            aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findResultData', undefined);
                            findResult.value = 'Введите текст';
                            setUserData();
                        },
                    },
                },
            ],
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'find-start',
            hint: 'Начать поиск',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-start-find-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'find-next',
            hint: 'Следующий',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-find-next-base',
        },
    },
];

const toolbarButtonClick = async e => {
    switch (e.item.component.name) {
        case 'refresh':
            await refresh(e);
            break;
        case 'find-prev':
            await findPrev(e);
            break;
        case 'find-start':
            await findStart(e);
            break;
        case 'find-next':
            await findNext(e);
            break;
        default:
            break;
    }
    emit('toolbarButtonClick', e);
};

const toolbarTextboxEvent = async e => {
    switch (e.eventName) {
        case 'enter-key':
            switch (e.item.component.name) {
                case 'find':
                    await onEnterKeyFilter(e);
                    break;
                default:
                    break;
            }
            break;
        case 'key-up':
            switch (e.item.component.name) {
                case 'find':
                    await onFilterKeyup(e);
                    break;
                default:
                    break;
            }
            break;
        default:
            break;
    }
    emit('toolbarTextboxEvent', e);
};
const getValueRefs = name => {
    if (refs.value[name]) {
        return refs.value[name];
    }
    return undefined;
};
const eventController = async e => {
    // if (!e.item.component.emits.find(item=>item.name === e.eventName)) {
    //   return;
    // }
    if (e.item.widget === 'dx-button') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
    if (e.item.widget === 'dx-text-box') {
        if (e.eventName === 'enter-key' || e.eventName === 'key-up') {
            await toolbarTextboxEvent(e);
        }
    }
};
// const sorting = ref({
//     selector: 'd-c',
//     desc: false,
// });

const dataSet = ref([]);

function getDataField(dataField) {
    if (dataField === firstColumn()) {
        return props.meta.firstColumnFields.text;
    }
    return dataField;
}

const onCellClick = async e => {
    if (e.rowType === 'header') {
        const field = getDataField(e.column.dataField);
        let order = 'asc';
        if (e.column.sortOrder !== undefined) {
            if (e.column.sortOrder === 'asc') {
                order = 'desc';
            }
        }
        sort.value = {
            field,
            order,
        };
        await refresh();
    }
};
const firstColumn = () => {
    return `first_column_${props.meta.firstColumnFields.text}`;
};

const treeListRef = ref(null);

const expandedRowKeys = ref([]);

const totalCount = ref(0);
const statusMessage = ref(null);
const onEnterKeyFilter = e => {};

const focusedID = ref(null);
const onFocusedRowChanged = e => {
    aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'focusedRowKey', focusedRowKey.value);
    if (e.row) {
        focusedID.value = e.row.data[props.meta.keyExpr];
    }
};

const setUserData = async () => {
    const userData = {};
    userData.changes = {
        findValue: findValue.value,
    };
    await saveUserDataChanges(userData);
};
const onFilterKeyup = async e => {
    findValue.value = e.event.currentTarget.value;
    await setUserData();
    aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findValue', findValue.value);
};

async function stopLoad() {
    loadStop.value = true;
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(loadStatus.value === 'non');
        }, 1000);
    });
}

const loadStatus = ref('non');
const loadStop = ref(false);
const total = () => {
    if (!dataSet.value) {
        return 0;
    }
    return dataSet.value.length;
};

const refreshLocal = async () => {
    const data = aiTreeNodeLoadStoreLocalGet(props.meta.api);
    if (!data) {
        await refresh();
    } else {
        dataSet.value = data.dataSet.dataset;
        totalCount.value = total();
        if (!data.findResultData) {
            findResultData.value = [];
        } else {
            findResultData.value = data.findResultData;
            findValue.value = data.findValue;
            await findFocused(findResultData.value[0]);
            findResult.value = `Найдено: ${findResultData.value.length} позиция [${findPosition.value + 1}]`;
        }
        if (data.focusedRowKey) {
            await setFocusedKey(data.focusedRowKey);
        } else {
            focusedRowKey.value = null;
        }
    }
};

const setFocusedKey = async key => {
    if (!key) {
        return;
    }
    const row = dataSet.value?.find(row => row[props.meta.keyExpr] === key);
    await findFocused(row);
};
const refresh = async () => {
    findResult.value = `Введите текст`;
    findValue.value = '';
    if (loadStatus.value !== 'none') {
        let stopped = false;
        while (!stopped) {
            stopped = await stopLoad();
            if (stopped) {
                loadStop.value = false;
            }
        }
    }
    loadStatus.value = 'first';
    if (treeListRef.value?.instance) {
        treeListRef.value.instance.beginCustomLoading();
    }
    expandedRowKeys.value = [];
    const selectFuncParams = await props.selectFuncParamsCallBack({
        option: 'roots',
        keyExprValue: null,
    });
    const fetchResult = await fetch(null, 'roots', props.meta.columnsLikes, 0, selectFuncParams, null, true);
    if (fetchResult.result) {
        dataSet.value = fetchResult.data;
        aiTreeNodeLoadStoreLocalSet(props.meta.api, {
            dataset: fetchResult.data,
            focusedRowKey: focusedRowKey.value,
        });
        if (treeListRef.value?.instance) {
            treeListRef.value.instance.endCustomLoading();
        }
        totalCount.value = total();
        loadStatus.value = 'non';
    } else {
        dataSet.value = [];
        totalCount.value = total();
        throw new Error(`api: ${fetchResult.api}, mes: ${fetchResult.mes}, err: ${fetchResult.err}`);
    }
};
const onRowExpanded = e => {};

const onRowExpanding = async e => {
    const node = treeListRef.value.instance.getNodeByKey(e.key);
    if (!node) {
        return;
    }
    if (node.children.length === 0 && !dataSet.value.find(item => item[props.meta.keyParent] === e.key)) {
        const selectFuncParams = await props.selectFuncParamsCallBack({
            option: 'childes',
            keyExprValue: e.key,
        });
        await props.rowExpandingCallBack(e);
        treeListRef.value.instance.beginCustomLoading();
        const fetchResult = await fetch(e.key, 'childes', props.meta.columnsLikes, node.level, selectFuncParams);
        dataSet.value = dataSet.value.concat(fetchResult.data);
        totalCount.value = total();
        treeListRef.value.instance.endCustomLoading();
    }
    aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'dataSet', dataSet.value);
};

const sort = ref(undefined);
const fetch = async (keyParentValue, option, columnsLikes, level, selectFuncParams, findValue, refresh) => {
    const args = {
        ...props.meta,
        columns: [
            ...props.meta.columns,
            props.meta.firstColumnFields
                ? {
                      field: props.meta.firstColumnFields.text,
                      caption: props.meta.firstColumnFields.caption,
                  }
                : null, // тернарный оператор
        ].filter(Boolean),
        keyParentValue,
        option,
        columnsLikes,
        level,
        selectFuncParams: selectFuncParams ? selectFuncParams : {},
        sort: sort.value,
        likeText: findValue,
        focusedId: focusedID.value,
    };
    const apiResp = await aiTreeNodeLoadStore(args, refresh);
    if (!apiResp.result) {
        throw new Error(JSON.stringify(apiResp));
    } else {
        return apiResp;
    }
};

const isDate = (column) => column.dataType === 'date' || column.dataType === 'datetime';

const findResultData = ref([]);

const focusedInFindResultData = findResultData => {
    for (const row of findResultData) {
        if (row[props.meta.keyExpr] === focusedID.value) {
            return row;
        }
    }
    return false;
};
const focusedInFindIndex = findResultData => {
    for (let i = 0; i < findResultData.length; i++) {
        if (findResultData[i][props.meta.keyExpr] === focusedID.value) {
            return i;
        }
    }
    return -1;
};
const findInFocus = findResultData => {
    for (const row of findResultData) {
        if (row[props.meta.keyExpr] === focusedID.value) {
            for (const field of props.meta.columnsLikes) {
                if (row[field].toLowerCase().includes(findValue.value.toString().toLowerCase())) {
                    return true;
                }
            }
        }
    }
    return false;
};
const getBeforeAndAfterFocusedCount = findResultData => {
    let before = true;
    const beforeArray = [];
    const afterArray = [];
    for (const row of findResultData) {
        if (row[props.meta.keyExpr] === focusedID.value) {
            before = false;
        } else {
            if (before) {
                beforeArray.push(row);
            } else {
                afterArray.push(row);
            }
        }
    }
    return { before: beforeArray, after: afterArray };
};

const findStart = async () => {
    expandedRowKeys.value = [];
    findPosition.value = null;
    if (findValue.value?.length > 0) {
        const selectFuncParams = await props.selectFuncParamsCallBack({ option: 'find' });
        findResult.value = 'Идет поиск...';
        treeListRef.value.instance.beginCustomLoading();
        disabledStart.value = true;
        disabledPrev.value = true;
        disabledNext.value = true;
        const fetchResult = await fetch(null, 'find', props.meta.columnsLikes, 0, selectFuncParams, findValue.value);
        findResultData.value = fetchResult.data;
        aiTreeNodeLoadStoreLocalSetProp(props.meta.api, 'findResultData', findResultData.value);
        if (fetchResult.data?.length > 0) {
            findPosition.value = 0;
            focusedRowKey.value = null;
            const focusedInFindResultDataResult = focusedInFindResultData(fetchResult.data);
            if (focusedInFindResultDataResult === false) {
                await findFocused(fetchResult.data[0]);
                findResult.value = `Найдено: ${fetchResult.data?.length} позиция [1]`;
            } else {
                await getResultText(findResultData.value, focusedInFindResultDataResult);
                // const findInFocusResult = findInFocus(fetchResult.data);
                // const beforeAndAfterFocusedCount = getBeforeAndAfterFocusedCount(fetchResult.data);
                // findResult.value = `▲:[${beforeAndAfterFocusedCount.before.length}] ●: [${findInFocusResult?1:0}] ▼: [${beforeAndAfterFocusedCount.after.length}]`;
                // await findFocused(focusedInFindResultDataResult);
                // findPosition.value = focusedInFindIndex(fetchResult.data);
            }
        } else {
            findResult.value = 'Найдено: 0';
        }
        disabledStart.value = false;
        disabledPrev.value = false;
        disabledNext.value = false;
        treeListRef.value.instance.endCustomLoading();
    } else {
        findResult.value = `Введите текст`;
    }
};

const getResultText = async (fetchResult, focusedRow) => {
    const findInFocusResult = findInFocus(fetchResult);
    const beforeAndAfterFocusedCount = getBeforeAndAfterFocusedCount(fetchResult);
    findResult.value = `▲:[${beforeAndAfterFocusedCount.before.length}] ●: [${findInFocusResult ? 1 : 0}] ▼: [${
        beforeAndAfterFocusedCount.after.length
    }]`;
    await findFocused(focusedRow);
    findPosition.value = focusedInFindIndex(fetchResult);
};

const focusedRowKey = ref(null);
const findPosition = ref(null);

const findFocused = async row => {
    const rowPath = row[props.meta.pathField];
    let level = 0;

    for (const key of rowPath) {
        const findInDataset = await dataSet.value.find(item => item[props.meta.keyExpr] === key);
        if (!findInDataset) {
            const index = rowPath.indexOf(key);
            const parent = rowPath[index - 1];
            const selectFuncParams = await props.selectFuncParamsCallBack({
                option: 'childes',
                keyExprValue: parent,
            });
            const fetchResult = await fetch(parent, 'childes', props.meta.columnsLikes, level, selectFuncParams);
            dataSet.value = dataSet.value.concat(fetchResult.data);
            level++;
        } else {
            console.log('find key: ', key);
        }
    }
    const findInDataset = await dataSet.value.find(item => item[props.meta.keyExpr] === row[props.meta.keyExpr]);
    if (!findInDataset) {
        const parent = rowPath[rowPath.length - 1];
        const selectFuncParams = await props.selectFuncParamsCallBack({
            option: 'childes',
            keyExprValue: parent,
        });
        const fetchResult = await fetch(parent, 'childes', props.meta.columnsLikes, level, selectFuncParams);
        dataSet.value = dataSet.value.concat(fetchResult.data);
    }
    const focusedKey = row[props.meta.keyExpr];
    const newPath = [...rowPath];
    //newPath.splice(newPath.length - 2, 1);
    for (const key of newPath) {
        treeListRef.value.instance.expandRow(key);
        focusedRowKey.value = key;
    }
    treeListRef.value.instance.navigateToRow(focusedKey);
    focusedRowKey.value = focusedKey;
    //
    // setTimeout(() => {
    //     focusedRowKey.value = focusedKey;
    // }, 1000);
};

const findNext = async () => {
    if (findPosition.value < findResultData.value.length - 1) {
        findPosition.value = findPosition.value + 1;
        const row = findResultData.value[findPosition.value];
        await findFocused(row);
        // findResult.value = `Найдено: ${findResultData.value.length} позиция [${findPosition.value + 1}]`;
        focusedID.value = row[props.meta.keyExpr];
        const focusedRow = focusedInFindResultData(findResultData.value);
        await getResultText(findResultData.value, focusedRow);
    }
};
const findPrev = async () => {
    if (findPosition.value > 0) {
        findPosition.value = findPosition.value - 1;
        const row = findResultData.value[findPosition.value];
        await findFocused(row);
        // findResult.value = `Найдено: ${findResultData.value.length} позиция [${findPosition.value + 1}]`;
        focusedID.value = row[props.meta.keyExpr];
        const focusedRow = focusedInFindResultData(findResultData.value);
        await getResultText(findResultData.value, focusedRow);
    }
};

const setStatusMessage = text => {
    statusMessage.value = text;
};

defineExpose({
    treeListRef,
    setStatusMessage,
});
</script>
<script>
export default {
    name: 'dx-tree-list-json-local-ds',
};
</script>

<style scoped>
.custom-toolbar {
    :deep(.dx-treelist-header-panel) {
        min-width: fit-content;
        width: 100%;
        display: flex;
    }

    :deep(.dx-toolbar-center:empty) {
        display: flex;
    }
}

:deep(.dx-clear-button-area) {
    margin-left: 8px !important; /* Внешний отступ от поля ввода */
}
</style>
